import cn from 'classnames';
import React from 'react';

import { useCheckoutContext } from '@zooplus/checkout-state';
import { H1 } from '@zooplus/ui-components';

import GridLayoutWrapper from '../grid-layout-wrapper';
import ApiErrorMessage from '../notifications/api-error-message/api-error-message';

import FeedbackMessage from '@components/common/notifications/feedback-message';
import { useIsResponsiveViewport } from '@hooks/use-is-responsive-viewport';
import { useTranslation } from '@hooks/use-translations';
import commonStyles from '@styles/common.module.scss';
import { MESSAGE_PLACEMENT, CHECKOUT_STATE_PROJECT_ID } from '@utilities/constants';
import { isMobileViewport } from '@utilities/utils';

interface PageCommonLayoutProps {
  pageHeading: string;
  leftColumnContent: React.ReactNode;
  rightColumnContent: React.ReactNode;
  recommendationContent?: React.ReactNode;
  mobileViewCheckoutButton?: React.ReactNode;
  feedbackMessage?: React.ReactNode;
  leftColumnAriaLabel?: string;
}

const PageCommonLayout: React.FC<PageCommonLayoutProps> = ({
  pageHeading,
  leftColumnContent,
  leftColumnAriaLabel,
  rightColumnContent,
  recommendationContent,
  mobileViewCheckoutButton,
  feedbackMessage,
}) => {
  const { isFetching, pageType } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const isResponsiveViewport = useIsResponsiveViewport();
  const { t } = useTranslation();

  const layoutWrapperContainerStyles = cn(
    commonStyles.layoutWrapperContainer,
    commonStyles.layoutWrapperContainer_wideLayout,
  );

  const leftColumnStyles = cn(commonStyles.leftColumn, commonStyles.leftColumn_wideLayout);

  const summaryCommonWrapperStyles = cn(
    commonStyles.summaryCommonWrapper,
    commonStyles.summaryCommonWrapper_wideLayout,
  );

  const recommendationWrapperStyles = cn(
    commonStyles.recommendationWrapper,
    commonStyles.recommendationWrapper_wideLayout,
  );

  const pageHeadingId = `${pageType}-heading`;

  return (
    <main
      aria-label={pageHeading}
      data-testid={pageType}
      className={commonStyles.pageLayoutWrapper}>
      <GridLayoutWrapper>
        <H1 data-testid={pageHeadingId} id={pageHeadingId} className="pp-mb-0">
          {pageHeading}
        </H1>
        <ApiErrorMessage />
        <section className={layoutWrapperContainerStyles}>
          <section className={leftColumnStyles} aria-label={leftColumnAriaLabel || pageHeading}>
            <FeedbackMessage
              placement={
                isMobileViewport()
                  ? MESSAGE_PLACEMENT.PAGE_HEADER_MOBILE
                  : MESSAGE_PLACEMENT.PAGE_HEADER
              }
              customStyle="pp-mb-xs"
              containerStyle="pp-pr-l pp-pl-l"
            />
            {leftColumnContent}
          </section>
          <section
            className={summaryCommonWrapperStyles}
            aria-label={t('cart.marketplace.orderSummary.label')}>
            {rightColumnContent}
          </section>
          {recommendationContent && (
            <section
              className={recommendationWrapperStyles}
              aria-label={t('cart.recommendations.slider.ariaLabel')}>
              {!isFetching && recommendationContent}
            </section>
          )}
        </section>
        {isResponsiveViewport && mobileViewCheckoutButton}
        {feedbackMessage}
      </GridLayoutWrapper>
    </main>
  );
};

export default PageCommonLayout;
